
import React from 'react';
import { Task, TaskStatus, User as UserType } from '../types';
import { Clock, CheckCircle2, MoreHorizontal, User, Calendar, ArrowRight, Edit3, Lock, Link as LinkIcon, UserCheck } from 'lucide-react';

interface TaskBoardProps {
  tasks: Task[];
  users: UserType[];
  onUpdateStatus: (taskId: string, status: TaskStatus) => void;
  onEditTask: (task: Task) => void;
}

const COLUMNS: TaskStatus[] = ['Pending', 'In Progress', 'Review', 'Completed'];

const TaskBoard: React.FC<TaskBoardProps> = ({ tasks, users, onUpdateStatus, onEditTask }) => {
  const getTasksByStatus = (status: TaskStatus) => tasks.filter(t => t.status === status);
  const getAssignee = (id?: string) => users.find(u => u.id === id);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'bg-rose-100 text-rose-700 border-rose-200';
      case 'Medium': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    }
  };

  return (
    <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-6 overflow-hidden pb-4">
      {COLUMNS.map(column => (
        <div key={column} className="flex flex-col min-h-0">
          <div className="flex items-center justify-between mb-4 px-2">
            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
              <span className={`w-1.5 h-1.5 rounded-full ${
                column === 'Pending' ? 'bg-slate-300' :
                column === 'In Progress' ? 'bg-blue-400' :
                column === 'Review' ? 'bg-purple-400' :
                column === 'Completed' ? 'bg-emerald-400' : 'bg-slate-400'
              }`}></span>
              {column}
            </h4>
            <span className="text-[10px] font-black text-slate-300 bg-slate-50 px-2 py-0.5 rounded-full border border-slate-100">
                {getTasksByStatus(column).length}
            </span>
          </div>

          <div className="flex-1 overflow-y-auto space-y-4 custom-scrollbar pr-1">
            {getTasksByStatus(column).map(task => {
              const assignee = getAssignee(task.assigneeId);

              return (
                <div 
                  key={task.id} 
                  className={`bg-white p-5 rounded-3xl border shadow-sm hover:shadow-md hover:border-indigo-300 transition-all group relative cursor-pointer border-slate-200`}
                  onClick={() => onEditTask(task)}
                >
                  <div className="flex justify-between items-start mb-3">
                    <span className={`px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-widest border ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                    </span>
                    <button className="text-slate-300 hover:text-indigo-600 transition-colors">
                      <MoreHorizontal size={16} />
                    </button>
                  </div>

                  <h5 className="font-bold text-slate-800 text-sm mb-2 leading-snug group-hover:text-indigo-600 transition-colors uppercase tracking-tight">{task.title}</h5>
                  
                  {task.description && <p className="text-xs text-slate-400 line-clamp-2 mb-4 leading-relaxed font-medium italic">{task.description}</p>}

                  <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-50">
                    <div className="flex items-center gap-2 px-2 py-1 bg-slate-50 rounded-xl border border-slate-100" title={assignee?.fullName || 'Unassigned'}>
                      {assignee?.avatarUrl ? (
                         <img src={assignee.avatarUrl} alt="" className="w-5 h-5 rounded-lg object-cover border border-white shadow-sm" />
                      ) : (
                         <UserCheck size={12} className="text-indigo-400" />
                      )}
                      <span className="text-[10px] font-black text-slate-600 truncate max-w-[80px] uppercase">
                        {assignee?.fullName.split(' ')[0] || 'Unassigned'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-[10px] font-black text-slate-400 uppercase">
                      <Calendar size={12} /> {task.dueDate}
                    </div>
                  </div>

                  <div className="absolute top-2 right-2 p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                     <div className="flex bg-white shadow-xl border border-slate-100 rounded-xl p-1 gap-1">
                        <button 
                          onClick={(e) => { e.stopPropagation(); onEditTask(task); }}
                          className="p-1.5 hover:bg-indigo-50 text-slate-400 hover:text-indigo-600 rounded-lg transition-colors"
                          title="Edit Task"
                        >
                          <Edit3 size={14} />
                        </button>
                        {COLUMNS.filter(c => c !== column).map(targetStatus => (
                           <button 
                             key={targetStatus}
                             onClick={(e) => { e.stopPropagation(); onUpdateStatus(task.id, targetStatus); }}
                             className="p-1.5 rounded-lg hover:bg-indigo-50 text-slate-400 hover:text-indigo-600"
                             title={`Move to ${targetStatus}`}
                           >
                             <ArrowRight size={14} className={targetStatus === 'Completed' ? 'text-emerald-500' : ''} />
                           </button>
                        ))}
                     </div>
                  </div>
                </div>
              );
            })}
            
            {getTasksByStatus(column).length === 0 && (
                <div className="py-12 border-2 border-dashed border-slate-100 rounded-[32px] flex flex-col items-center justify-center text-slate-200">
                    <CheckCircle2 size={32} className="opacity-10 mb-2" />
                    <span className="text-[10px] font-black uppercase tracking-widest opacity-30 tracking-[0.2em]">All Clear</span>
                </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default TaskBoard;
